import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  Camera,
  CheckCircle2,
  Loader2,
  Play,
  Video,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ProofStandard } from "@/hooks/useProofStandards";
import { useResolvedStorageUrls } from "@/hooks/useResolvedStorageUrls";
import {
  buildUnitCheckWalkSteps,
  buildUnitCheckWalkthrough,
  type UnitCheckWalkStep,
} from "@/lib/proofStandardsDisplay";
import {
  isUnitCheckSatisfied,
  proofUrlsForUnitCheck,
  unitCheckProofKey,
} from "@/lib/unitCheckProofKeys";
import { UnitItemProofTypeBadge } from "@/components/cleaner/ProofStandardHostDetailContent";
import { UnitCheckIssueReport } from "@/components/cleaner/UnitCheckIssueReport";
import { CleanerMediaLightbox } from "@/components/cleaner/CleanerMediaLightbox";

const MEDIA_BOX =
  "relative mx-auto h-44 w-44 overflow-hidden rounded-2xl border shadow-[var(--pv-shadow-card)] sm:h-48 sm:w-48";

export interface UnitCheckRoomWalkthroughProps {
  standards: ProofStandard[];
  checklistRoomNames: string[];
  proofMediaUrlsByStandardId: Record<string, string[]>;
  functionalPassByKey?: Record<string, boolean>;
  onToggleFunctionalPass?: (standardId: string, roomName: string, nextPassed: boolean) => void;
  proofUploadingStandardId: string | null;
  submitting: boolean;
  extraDirty: "no" | "yes";
  onOpenReference: (s: ProofStandard) => void;
  onPickFiles: (standardId: string, roomName: string, files: FileList | null) => void;
  onRemoveProofUrl: (standardId: string, roomName: string, url: string) => void;
  isVideoUrl: (url: string) => boolean;
  t: (key: string) => string;
  tDynamic: (s: string) => string;
  propertyId: string;
  cleaningTaskId: string;
  cleanerId: string;
  checklistName?: string | null;
}

export function UnitCheckRoomWalkthrough({
  standards,
  checklistRoomNames,
  proofMediaUrlsByStandardId,
  functionalPassByKey = {},
  onToggleFunctionalPass,
  proofUploadingStandardId,
  submitting,
  extraDirty,
  onOpenReference,
  onPickFiles,
  onRemoveProofUrl,
  isVideoUrl,
  t,
  tDynamic,
  propertyId,
  cleaningTaskId,
  cleanerId,
  checklistName = null,
}: UnitCheckRoomWalkthroughProps) {
  const generalLabel = tDynamic("General");
  const sections = useMemo(
    () => buildUnitCheckWalkthrough(standards, checklistRoomNames, generalLabel),
    [standards, checklistRoomNames, generalLabel],
  );
  const walkSteps = useMemo(() => buildUnitCheckWalkSteps(sections), [sections]);

  const [stepIndex, setStepIndex] = useState(0);
  const hydratedRef = useRef(false);

  const currentStep: UnitCheckWalkStep | undefined = walkSteps[stepIndex];
  const taskSteps = useMemo(
    () => walkSteps.filter((s): s is Extract<UnitCheckWalkStep, { kind: "task" }> => s.kind === "task"),
    [walkSteps],
  );
  const totalTasks = taskSteps.length;
  const completedTasks = useMemo(
    () =>
      taskSteps.filter((step) =>
        isUnitCheckSatisfied(
          step.standard,
          step.roomName,
          proofMediaUrlsByStandardId,
          functionalPassByKey,
        ),
      ).length,
    [taskSteps, proofMediaUrlsByStandardId, functionalPassByKey],
  );

  useEffect(() => {
    if (hydratedRef.current || walkSteps.length === 0) return;
    hydratedRef.current = true;
    const firstOpen = walkSteps.findIndex((step) => {
      if (step.kind !== "task") return false;
      return !isUnitCheckSatisfied(
        step.standard,
        step.roomName,
        proofMediaUrlsByStandardId,
        functionalPassByKey,
      );
    });
    if (firstOpen >= 0) setStepIndex(firstOpen);
  }, [walkSteps, proofMediaUrlsByStandardId, functionalPassByKey]);

  const hasAnyHostReference = useMemo(
    () =>
      standards.some(
        (s) =>
          (s.reference_media_urls?.length ?? 0) > 0 || Boolean(s.reference_youtube_url?.trim()),
      ),
    [standards],
  );

  if (standards.length === 0) return null;

  const goNext = () => {
    if (stepIndex < walkSteps.length - 1) setStepIndex((i) => i + 1);
  };

  if (!currentStep) {
    return (
      <div
        className="rounded-[var(--pv-r-xl)] border bg-[var(--pv-surface)] p-6 text-center shadow-[var(--pv-shadow-card)]"
        style={{ borderColor: "var(--pv-teal)" }}
      >
        <CheckCircle2 className="mx-auto mb-3 h-10 w-10" style={{ color: "var(--pv-teal)" }} />
        <p className="text-base font-semibold" style={{ color: "var(--pv-ink)" }}>
          {tDynamic("Unit check complete")}
        </p>
        <p className="mt-1 text-sm" style={{ color: "var(--pv-ink-2)" }}>
          {tDynamic("Tap continue below to start cleaning.")}
        </p>
      </div>
    );
  }

  if (currentStep.kind === "room_transition") {
    return (
      <div
        className="rounded-[var(--pv-r-xl)] border bg-[var(--pv-surface)] p-6 shadow-[var(--pv-shadow-card)]"
        style={{ borderColor: "var(--pv-line)" }}
      >
        <p className="text-center text-[11px] font-bold uppercase tracking-[0.1em]" style={{ color: "var(--pv-ink-3)" }}>
          {tDynamic("Room")} {currentStep.roomIndex + 1} / {currentStep.roomCount} {tDynamic("done")}
        </p>
        <div className="my-6 flex flex-col items-center gap-3 text-center">
          <div
            className="flex h-14 w-14 items-center justify-center rounded-full"
            style={{ background: "var(--pv-teal-soft)" }}
          >
            <ArrowRight className="h-7 w-7" style={{ color: "var(--pv-teal)" }} aria-hidden />
          </div>
          <p className="text-lg font-bold leading-snug" style={{ color: "var(--pv-ink)" }}>
            {tDynamic("Moving to")} {currentStep.nextRoomName}
          </p>
          <p className="text-sm" style={{ color: "var(--pv-ink-2)" }}>
            {tDynamic("Walk to the next room and continue your unit check.")}
          </p>
        </div>
        <Button
          type="button"
          className="h-12 w-full rounded-full text-base font-semibold"
          style={{ background: "var(--pv-teal)", color: "var(--pv-ink-on-accent)" }}
          onClick={goNext}
        >
          {tDynamic("Continue")}
        </Button>
      </div>
    );
  }

  return (
    <UnitCheckTaskScreen
      step={currentStep}
      completedTasks={completedTasks}
      totalTasks={totalTasks}
      extraDirty={extraDirty}
      hasAnyHostReference={hasAnyHostReference}
      proofMediaUrlsByStandardId={proofMediaUrlsByStandardId}
      functionalPassByKey={functionalPassByKey}
      onToggleFunctionalPass={onToggleFunctionalPass}
      proofUploadingStandardId={proofUploadingStandardId}
      submitting={submitting}
      onOpenReference={onOpenReference}
      onPickFiles={onPickFiles}
      onRemoveProofUrl={onRemoveProofUrl}
      isVideoUrl={isVideoUrl}
      t={t}
      tDynamic={tDynamic}
      propertyId={propertyId}
      cleaningTaskId={cleaningTaskId}
      cleanerId={cleanerId}
      checklistName={checklistName}
      onContinue={goNext}
      canContinue={isUnitCheckSatisfied(
        currentStep.standard,
        currentStep.roomName,
        proofMediaUrlsByStandardId,
        functionalPassByKey,
      )}
      isLastStep={stepIndex >= walkSteps.length - 1}
    />
  );
}

type UnitCheckTaskScreenProps = {
  step: Extract<UnitCheckWalkStep, { kind: "task" }>;
  completedTasks: number;
  totalTasks: number;
  extraDirty: "no" | "yes";
  hasAnyHostReference: boolean;
  proofMediaUrlsByStandardId: Record<string, string[]>;
  functionalPassByKey: Record<string, boolean>;
  onToggleFunctionalPass?: (standardId: string, roomName: string, nextPassed: boolean) => void;
  proofUploadingStandardId: string | null;
  submitting: boolean;
  onOpenReference: (s: ProofStandard) => void;
  onPickFiles: (standardId: string, roomName: string, files: FileList | null) => void;
  onRemoveProofUrl: (standardId: string, roomName: string, url: string) => void;
  isVideoUrl: (url: string) => boolean;
  t: (key: string) => string;
  tDynamic: (s: string) => string;
  propertyId: string;
  cleaningTaskId: string;
  cleanerId: string;
  checklistName: string | null;
  onContinue: () => void;
  canContinue: boolean;
  isLastStep: boolean;
};

function UnitCheckTaskScreen({
  step,
  completedTasks,
  totalTasks,
  extraDirty,
  hasAnyHostReference,
  proofMediaUrlsByStandardId,
  functionalPassByKey,
  onToggleFunctionalPass,
  proofUploadingStandardId,
  submitting,
  onOpenReference,
  onPickFiles,
  onRemoveProofUrl,
  isVideoUrl,
  t,
  tDynamic,
  propertyId,
  cleaningTaskId,
  cleanerId,
  checklistName,
  onContinue,
  canContinue,
  isLastStep,
}: UnitCheckTaskScreenProps) {
  const s = step.standard;
  const roomName = step.roomName;
  const proofKey = unitCheckProofKey(s.id, roomName);
  const urls = proofUrlsForUnitCheck(proofMediaUrlsByStandardId, s.id, roomName);
  const passedFunctional = !!functionalPassByKey[proofKey] || !!functionalPassByKey[s.id];
  const refs = s.reference_media_urls || [];
  const resolvedRefs = useResolvedStorageUrls(refs);
  const hostYoutube = s.reference_youtube_url?.trim() ?? "";
  const hasProof = urls.length > 0;
  const satisfied = canContinue;
  const isFunctional = s.type === "functional_check" && !!onToggleFunctionalPass;
  const isUploadingThis = proofUploadingStandardId === proofKey;
  const accept = s.type === "proof_video" ? "video/*,image/*" : "image/*,video/*";
  const fileInputId = `unit-check-walk-${s.id}`;
  const hero = hasProof ? urls[urls.length - 1] : null;
  const instructions = s.cleaner_instructions?.trim() ?? "";
  const [referenceLightbox, setReferenceLightbox] = useState<{ urls: string[]; index: number } | null>(null);

  return (
    <div
      className="rounded-[var(--pv-r-xl)] border bg-[var(--pv-surface)] p-4 shadow-[var(--pv-shadow-card)]"
      style={{ borderColor: "var(--pv-line)" }}
    >
      <div
        className="mb-3 flex gap-2 rounded-xl border px-3 py-2.5 text-xs font-medium leading-snug"
        style={{ borderColor: "var(--pv-teal)", background: "var(--pv-teal-soft)", color: "var(--pv-teal)" }}
      >
        {t("preVisitChecklistOpensAfterSave")}
      </div>

      {extraDirty === "yes" && hasAnyHostReference ? (
        <div
          className="mb-3 rounded-xl border px-3 py-2.5 text-xs font-medium leading-snug"
          style={{ borderColor: "var(--pv-warn)", background: "var(--pv-warn-soft)", color: "var(--pv-warn)" }}
        >
          {t("preVisitReferenceBannerExtraDirty")}
        </div>
      ) : null}

      <div className="mb-4 space-y-1">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: "var(--pv-teal)" }}>
          {tDynamic(step.roomName)} · {tDynamic("Room")} {step.roomIndex + 1}/{step.roomCount}
        </p>
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-bold leading-tight" style={{ color: "var(--pv-ink)" }}>
            {tDynamic(step.roomName)}
          </h2>
          <span className="shrink-0 text-sm font-semibold tabular-nums" style={{ color: "var(--pv-ink-3)" }}>
            {completedTasks}/{totalTasks}
          </span>
        </div>
        <p className="text-xs" style={{ color: "var(--pv-ink-2)" }}>
          {tDynamic("Item")} {step.taskIndex + 1} {tDynamic("of")} {step.taskCount} {tDynamic("in this room")}
        </p>
        <div className="h-1.5 w-full overflow-hidden rounded-full" style={{ background: "var(--pv-line-2)" }}>
          <div
            className="h-full rounded-full transition-[width] duration-300"
            style={{
              width: `${totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0}%`,
              backgroundColor: "var(--pv-teal)",
            }}
          />
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className="rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
            style={{
              background: s.required_every_turnover ? "var(--pv-teal-soft)" : "var(--pv-surface-2)",
              color: s.required_every_turnover ? "var(--pv-teal)" : "var(--pv-ink-2)",
            }}
          >
            {s.required_every_turnover ? t("unitItemsRequiredBadge") : t("unitItemsOptionalBadge")}
          </span>
          <UnitItemProofTypeBadge
            type={s.type}
            t={t}
            className="border-[color:var(--pv-line)] bg-[var(--pv-surface-2)] text-[var(--pv-ink-3)]"
            iconClassName="text-[var(--pv-ink)]"
          />
        </div>

        <p className="text-base font-semibold leading-snug" style={{ color: "var(--pv-ink)" }}>
          {tDynamic(s.title)}
        </p>
        {(s.rooms?.length ?? 0) > 1 ? (
          <p className="text-xs font-medium" style={{ color: "var(--pv-teal)" }}>
            {tDynamic("Same check in")} {s.rooms.length} {tDynamic("rooms")} — {tDynamic("now in")} {roomName}
          </p>
        ) : null}

        {instructions ? (
          <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: "var(--pv-ink-2)" }}>
            {instructions}
          </p>
        ) : (
          <p className="text-xs italic" style={{ color: "var(--pv-ink-3)" }}>
            {t("unitItemsNoHostNotes")}
          </p>
        )}

        {resolvedRefs.length > 0 || hostYoutube ? (
          <div className="space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-wide" style={{ color: "var(--pv-ink-3)" }}>
              {t("preVisitHostReferenceStrip")}
            </p>
            <div className="flex flex-wrap gap-2">
              {resolvedRefs.map((url, refIdx) => (
                <button
                  key={`${refs[refIdx] ?? url}-${refIdx}`}
                  type="button"
                  className="pv-tappable relative h-20 w-20 overflow-hidden rounded-xl border sm:h-24 sm:w-24"
                  style={{ borderColor: "var(--pv-line)" }}
                  onClick={() => setReferenceLightbox({ urls: resolvedRefs, index: refIdx })}
                >
                  {isVideoUrl(url) ? (
                    <video src={url} className="h-full w-full object-cover" muted playsInline />
                  ) : (
                    <img src={url} alt="" className="h-full w-full object-cover" />
                  )}
                  <span className="pointer-events-none absolute bottom-0 left-0 right-0 bg-black/55 py-0.5 text-center text-[8px] font-semibold uppercase text-white">
                    {tDynamic("Ref")}
                  </span>
                </button>
              ))}
              {hostYoutube && resolvedRefs.length === 0 ? (
                <button
                  type="button"
                  className="flex h-20 w-20 items-center justify-center rounded-xl border bg-[#FF0000]/10 sm:h-24 sm:w-24"
                  style={{ borderColor: "var(--pv-line)" }}
                  onClick={() => onOpenReference(s)}
                >
                  <Play className="h-8 w-8 fill-[#FF0000] text-[#FF0000]" />
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className={MEDIA_BOX} style={{ borderColor: satisfied ? "var(--pv-teal)" : "var(--pv-line)" }}>
          <input
            id={fileInputId}
            type="file"
            accept={accept}
            multiple
            className="hidden"
            onChange={(e) => {
              onPickFiles(s.id, roomName, e.target.files);
              e.target.value = "";
            }}
          />
          {isFunctional ? (
            <div className="absolute inset-0 z-[8] flex flex-col">
              <button
                type="button"
                disabled={submitting}
                className="pv-tappable flex flex-1 flex-col items-center justify-center gap-1 border-b"
                style={{
                  borderColor: "var(--pv-line)",
                      background: passedFunctional ? "var(--pv-teal-soft)" : "transparent",
                    }}
                    onClick={() => onToggleFunctionalPass!(s.id, roomName, !passedFunctional)}
                  >
                    <CheckCircle2
                      className="h-6 w-6"
                      style={{ color: passedFunctional ? "var(--pv-teal)" : "var(--pv-ink-3)" }}
                />
                <span className="text-xs font-semibold">{tDynamic("Pass")}</span>
              </button>
              <button
                type="button"
                disabled={submitting || isUploadingThis}
                className="pv-tappable flex flex-1 flex-col items-center justify-center gap-1"
                onClick={() => document.getElementById(fileInputId)?.click()}
              >
                {isUploadingThis ? (
                  <Loader2 className="h-6 w-6 pv-spin" style={{ color: "var(--pv-teal)" }} />
                ) : hasProof && hero ? (
                  isVideoUrl(hero) ? (
                    <video src={hero} className="max-h-[50%] w-full object-cover" muted playsInline />
                  ) : (
                    <img src={hero} alt="" className="max-h-[50%] w-full object-cover" />
                  )
                ) : (
                  <>
                    <Camera className="h-6 w-6" style={{ color: "var(--pv-ink-3)" }} />
                    <span className="text-xs font-semibold">{tDynamic("Photo")}</span>
                  </>
                )}
              </button>
            </div>
          ) : (
            <button
              type="button"
              disabled={submitting || isUploadingThis}
              className="pv-tappable absolute inset-0 flex flex-col items-center justify-center gap-2 disabled:opacity-60"
              onClick={() => document.getElementById(fileInputId)?.click()}
            >
              {isUploadingThis ? (
                <Loader2 className="h-8 w-8 pv-spin" style={{ color: "var(--pv-teal)" }} />
              ) : hasProof && hero ? (
                isVideoUrl(hero) ? (
                  <video src={hero} className="h-full w-full object-cover" muted playsInline />
                ) : (
                  <img src={hero} alt="" className="h-full w-full object-cover" />
                )
              ) : (
                <>
                  {s.type === "proof_video" ? (
                    <Video className="h-8 w-8" style={{ color: "var(--pv-ink-3)" }} />
                  ) : (
                    <Camera className="h-8 w-8" style={{ color: "var(--pv-ink-3)" }} />
                  )}
                  <span className="text-xs font-semibold">
                    {s.type === "proof_video" ? tDynamic("Add video") : tDynamic("Add photo")}
                  </span>
                </>
              )}
            </button>
          )}
          {satisfied ? (
            <div
              className="pointer-events-none absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full shadow-md"
              style={{ background: "var(--pv-teal)", color: "var(--pv-ink-on-accent)" }}
            >
              <CheckCircle2 className="h-4 w-4" />
            </div>
          ) : null}
          {hasProof && urls.length === 1 ? (
            <button
              type="button"
              className="pv-tappable absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-full border bg-[var(--pv-surface)] shadow-md"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveProofUrl(s.id, roomName, urls[0]!);
              }}
              aria-label={tDynamic("Remove")}
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => onOpenReference(s)}>
          {t("unitItemsHostNotesLink")}
        </Button>

        <UnitCheckIssueReport
          standard={s}
          propertyId={propertyId}
          cleaningTaskId={cleaningTaskId}
          cleanerId={cleanerId}
          checklistName={checklistName}
          roomNameOverride={roomName}
          tDynamic={tDynamic}
          disabled={submitting}
        />

        <Button
          type="button"
          className="h-12 w-full rounded-full text-base font-semibold"
          style={{
            background: canContinue ? "var(--pv-teal)" : "var(--pv-line)",
            color: canContinue ? "var(--pv-ink-on-accent)" : "var(--pv-ink-3)",
          }}
          disabled={!canContinue || submitting || isUploadingThis}
          onClick={onContinue}
        >
          {isLastStep && canContinue
            ? tDynamic("Finish unit check")
            : tDynamic("Next")}
        </Button>
      </div>

      <CleanerMediaLightbox
        open={referenceLightbox !== null}
        onOpenChange={(next) => {
          if (!next) setReferenceLightbox(null);
        }}
        urls={referenceLightbox?.urls ?? []}
        initialIndex={referenceLightbox?.index ?? 0}
      />
    </div>
  );
}
